import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { BASE_URL } from "../../redux/ApiEndPoints";
import ProgressPointer from "../../assets/images/progress-pointer.svg";
import ProgressCircleBars from "../../assets/images/progress-circle-bars.svg";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

export default function StudentPerformanceCard() {
  const auth = useSelector((state) => state.auth);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPerformance = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}student/performance`, {
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setPerformance(data.data);
          }
        }
      } catch (error) {
        console.error("Error fetching performance:", error);
      } finally {
        setLoading(false);
      }
    };

    if (auth.token) {
      fetchPerformance();
    }
  }, [auth.token]);

  if (loading) {
    return (
      <article className="card-default-style h-100 student-progress-holder d-flex flex-column align-items-center justify-content-center">
        <div className="dottedLoading theme-color"></div>
      </article>
    );
  }

  // Use default values when no performance data
  const displayPerformance = performance || {
    overallScore: 0,
    performanceLevel: "Beginner"
  };

  const getSpeedometerColor = (score) => {
    if (score >= 90) return "#82bd00";
    if (score >= 70) return "#f0c20c";
    if (score >= 40) return "#f05203";
    if (score > 0) return "#6c757d"; // Gray for low scores
    return "#dee2e6"; // Light gray for zero score
  };

  const getSpeedometerTrailColor = (score) => {
    if (score >= 90) return "#d4edda"; // Light Green
    if (score >= 80) return "#d1ecf1"; // Light Blue
    if (score >= 70) return "#fff3cd"; // Light Yellow
    if (score >= 60) return "#ffeaa7"; // Light Orange
    if (score >= 40) return "#f8d7da"; // Light Red
    return "#e9ecef"; // Light Gray
  };

  const speedometerColor = getSpeedometerColor(displayPerformance?.overallScore);

  return (
    <div className="card-default-style h-100">
      <div className="graph-labels">
        <div className="d-flex me-20">
          <div className="label-box label-green me-10"></div>
          <span className="text-black ah-text-sm fw-semibold">
            Point Progress
          </span>
        </div>
      </div>
      <article className="h-100 student-progress-holder d-flex flex-column align-items-center justify-content-center">
        {/* Original Speedometer Design */}
        <div className="img-holder text-center position-relative mb-40">
          <CircularProgressbar
            value={displayPerformance?.overallScore}
            circleRatio={0.75}
            strokeWidth={8}
            styles={buildStyles({
              strokeLinecap: "round",
              pathColor: speedometerColor,
              trailColor: "#EAEAEA",
              rotation: 1 / 2 + 1 / 8,
            })}
          />
          <img src={ProgressCircleBars} alt="" className="img-fluid" />
          <div
            className="pointer-img"
            style={{
              transform: `translate(-50%, -50%) rotate(${
                displayPerformance?.overallScore * 2.7 - 135
              }deg)`,
              transformOrigin: "center",
              transition: "transform 1s ease-in-out",
            }}
          >
            <img src={ProgressPointer} alt="" className="img-fluid" />
          </div>
        </div>

        {/* Performance Score */}
        <div className="d-flex gap-2 align-items-center">
          <span className="ah-heading-05">Your Score:</span>
          <b
            className="ah-heading-04 text-black"
            style={{ color: speedometerColor }}
          >
            {displayPerformance?.overallScore}/100
          </b>
        </div>

        {/* Performance Level */}
        <div className="mt-8">
          <span
            className="badge ah-text-sm text-white px-14 py-6"
            style={{
              background: '#6c757d',
            }}
          >
            {displayPerformance?.performanceLevel}
          </span>
        </div>

        {/* Show message when no performance data */}
        {!performance && (
          <div className="mt-3 text-center">
            <p className="text-muted small mb-0">
              Start learning to see your performance
            </p>
          </div>
        )}
      </article>
    </div>
  );
}
